import * as _ from "lodash";
import { put, select } from "redux-saga/effects";

import actions from "@/actions";
import type { ModeName, ModeProps } from "@/types";

interface ChangeModePayload {
    name: ModeName;
    props?: ModeProps;
}

interface MapState {
    map: {
        values: {
            global: {
                present: { lights: unknown; markers: unknown };
            };
        };
    };
}

/**
 * Returns the information message to display for a given map mode.
 *
 * @param name - The map mode name.
 * @param props - Additional mode properties.
 * @returns The information message for the mode.
 */
export const getInformationMessageByMode = (
    name: ModeName,
    props?: ModeProps,
): string => {
    switch (name) {
        case "add": {
            if (props?.markerType) {
                return "Tap to place the marker.";
            }
            return "Tap  to place the light.";
        }
        case "select": {
            if (props?.type === "Draw") {
                return "Tap on an unselected light and hold down as you drag around the map to add to your selection. Any light you touch will be selected. Release when done selecting. Then grab a selected light to move the group.";
            }
            if (props?.type === "Rectangle") {
                return "Press and drag from the desired starting point to create and size the selection area. Release to finish and selecting lights inside or touching the selection area.";
            }
            return "Tap  to select a light or marker.";
        }
        case "highlight":
            return `Tap a light on the map to highlight the desired ${_.lowerCase(props?.direction)}`;
        case "group":
            return "Click anywhere to continue drawing, click the first vertex to complete, and right-click to undo.";
        case "group-edit":
            return "Click and drag the vertices to adjust the shape, click an edge to add a new vertex, and double-click a vertex to remove it.";
        case "firmware":
            return "Lights are colored by firmware version.";
        default: {
            return "";
        }
    }
};

/**
 * Handles map mode transitions, initializing mode-specific state and information messages.
 *
 * @param root0 - The action.
 * @param root0.payload - The mode transition payload.
 * @yields Selects state and dispatches mode transition actions.
 */
function* changeMode({ payload }: { payload: ChangeModePayload }) {
    try {
        const { name, props } = payload;

        if (name === "select") {
            const { lights, markers } = yield select(
                (state: MapState) => state.map.values.global.present,
            );

            yield put(actions.map.values.mode.setLights(lights));
            yield put(actions.map.values.mode.setMarkers(markers));
            yield put(actions.map.values.mode.setInitHistory());
        }

        if (name === "add") {
            yield put(actions.map.general.clearArrayOfLights());
        }

        const information = getInformationMessageByMode(name, props);

        yield put(actions.window.setInformation(information));
    } catch (err) {
        // eslint-disable-next-line no-console
        console.log("err on changing mode:", err);
    }
}

export default changeMode;
